import React, { Fragment, FunctionComponent, useEffect, useState } from 'react';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { TrackingKeys } from 'utils/api/models/eshop/tracking';
import { IGetOrderTrackingResponse } from 'services/orderTracking/orderTracking';
import { getLanguage } from 'utils/language/language';
import { translate } from 'components/Translation';
import styles from 'assets/jss/material-dashboard-pro-react/views/bulkPurchase/trackingStepperStyle';
import { makeStyles } from '@material-ui/core/styles';
import { CheckCircle } from '@material-ui/icons';
import { RadioButtonUnchecked } from '@material-ui/icons';
import { Adjust } from '@material-ui/icons';
import { successColor, warningColor } from 'assets/jss/material-dashboard-pro-react';
import { HeadingM, HeadingLBold } from 'components/Typography/Typography';

// @ts-ignore
const useStyles = makeStyles(styles);

const STEPS_MAP = new Map<
	TrackingKeys,
	{ title: string; doneTitle?: string; description: string; doneDescription?: string }
>([
	[
		'ORDER_RECEIVED',
		{
			title: 'tracking.title.ORDER_RECEIVED',
			description: 'tracking.description.ORDER_RECEIVED',
		},
	],
	[
		'REQUEST_FROM_SUPPLIER',
		{
			title: 'tracking.title.REQUEST_FROM_SUPPLIER',
			description: 'tracking.description.REQUEST_FROM_SUPPLIER',
		},
	],
	[
		'PREPARED',
		{
			title: 'tracking.title.PREPARED',
			doneTitle: 'tracking.doneTitle.PREPARED',
			description: 'tracking.description.PREPARED',
			doneDescription: 'tracking.doneDescription.PREPARED',
		},
	],
	[
		'SHIPPED',
		{
			title: 'tracking.title.SHIPPED',
			doneTitle: 'tracking.doneTitle.SHIPPED',
			description: 'tracking.description.SHIPPED',
		},
	],
	[
		'DELIVERED',
		{
			title: 'tracking.title.DELIVERED',
			doneTitle: 'tracking.doneTitle.DELIVERED',
			description: '',
			doneDescription: 'tracking.doneDescription.DELIVERED',
		},
	],
	[
		'REQUEST_RECEIVED',
		{
			title: 'tracking.title.REQUEST_RECEIVED',
			doneTitle: 'tracking.doneTitle.REQUEST_RECEIVED',
			description: '',
			doneDescription: 'tracking.doneDescription.REQUEST_RECEIVED',
		},
	],
	[
		'REQUEST_REJECTED',
		{
			title: 'tracking.title.REQUEST_REJECTED',
			doneTitle: 'tracking.doneTitle.REQUEST_REJECTED',
			description: '',
			doneDescription: 'tracking.doneDescription.REQUEST_REJECTED',
		},
	],
	[
		'PROCCESSING',
		{
			title: 'tracking.title.PROCCESSING',
			doneTitle: 'tracking.doneTitle.PROCCESSING',
			description: 'tracking.description.PROCCESSING',
			doneDescription: 'tracking.doneDescription.PROCCESSING',
		},
	],
	[
		'FULFILLMENT_IN_PROGRESS',
		{
			title: 'tracking.title.FULFILLMENT_IN_PROGRESS',
			doneTitle: 'tracking.doneTitle.FULFILLMENT_IN_PROGRESS',
			description: 'tracking.description.FULFILLMENT_IN_PROGRESS',
			doneDescription: 'tracking.doneDescription.FULFILLMENT_IN_PROGRESS',
		},
	],
	[
		'INSTALLATION',
		{
			title: 'tracking.title.INSTALLATION',
			doneTitle: 'tracking.doneTitle.INSTALLATION',
			description: '',
			doneDescription: 'tracking.doneDescription.INSTALLATION',
		},
	],
	[
		'INSTALLATION_COMPLETED',
		{
			title: 'tracking.title.INSTALLATION_COMPLETED',
			doneTitle: 'tracking.doneTitle.INSTALLATION_COMPLETED',
			description: '',
			doneDescription: 'tracking.doneDescription.INSTALLATION_COMPLETED',
		},
	],
	[
		'REQUEST_COMPLETED',
		{
			title: 'tracking.doneTitle.REQUEST_COMPLETED',
			description: '',
			doneDescription: 'tracking.doneDescription.REQUEST_COMPLETED',
		},
	],
	[
		'REQUEST_ACCEPTED',
		{
			title: 'tracking.doneTitle.REQUEST_ACCEPTED',
			doneTitle: 'tracking.doneTitle.REQUEST_ACCEPTED',
			description: '',
			doneDescription: 'tracking.doneDescription.REQUEST_ACCEPTED',
		},
	],
	[
		'READY_TO_PICKUP',
		{
			title: 'tracking.title.READY_TO_PICKUP',
			description: 'tracking.description.READY_TO_PICKUP',
		},
	],
	[
		'COLLECTED',
		{
			title: 'tracking.title.COLLECTED',
			description: 'tracking.description.COLLECTED',
		},
	],
]);

interface ITrackingStepper {
	orderTracking: IGetOrderTrackingResponse;
}

const TrackingStepper: FunctionComponent<ITrackingStepper> = ({ orderTracking }) => {
	const classes = useStyles();

	const getStatus = (status1: string, status2: string) => {
		if (status1 && status2) {
			return 'done';
		}
		if (status1 && !status2) {
			return 'inProgress';
		}
		return 'waiting';
	};

	return (
		<div className={classes.trackingStepperContainer}>
			<HeadingLBold>{translate('status')}</HeadingLBold>
			{orderTracking.tracking.map(({ status, statusTime }, index) => {
				const { title, doneTitle, description, doneDescription } = STEPS_MAP.get(status) ?? {};
				const nextStatusTime = orderTracking.tracking[index + 1]?.statusTime;
				const isLast = orderTracking.tracking.length === index + 1;
				return (
					<Fragment key={status}>
						<ConditionalRender
							show={index === 0}
							onTrue={() => (
								<>
									<div style={{ marginLeft: '32px' }} className={classes.descriptionWrapper}>
										{statusTime}
									</div>
								</>
							)}
						/>
						<div className={classes.statusContainer}>
							<div className={classes.statusIcon}>
								<ConditionalRender
									show={!!statusTime}
									onTrue={() => <CheckCircle htmlColor={successColor[2]} />}
									onFalse={() => (
										<ConditionalRender
											show={!statusTime && !!orderTracking.tracking[index - 1]?.statusTime}
											onTrue={() => <Adjust htmlColor={warningColor[0]} />}
											onFalse={() => <RadioButtonUnchecked />}
										/>
									)}
								/>
							</div>
							<div className={classes.statusWrapper}>
								<HeadingM>
									{statusTime && doneTitle ? translate(doneTitle) : title && translate(title)}
								</HeadingM>
							</div>
						</div>
						<div className={classes.statusContainer}>
							<ConditionalRender
								show={!isLast}
								onTrue={() => (
									<div className={classes.lineContainer}>
										<div className={classes[`line${getStatus(statusTime, nextStatusTime)}`]}></div>
									</div>
								)}
							/>
							<div className={classes.descriptionContainer}>
								<div className={classes.descriptionWrapper}>
									{statusTime && doneDescription
										? translate(doneDescription)
										: description && translate(description)}
								</div>
								<ConditionalRender
									show={!isLast}
									onTrue={() => (
										<div className={classes.descriptionWrapper}>
											{nextStatusTime
												? nextStatusTime
												: getStatus(statusTime, nextStatusTime) === 'inProgress' &&
												  !(status === 'DELIVERED')
												? translate('tracking.inProgress')
												: ''}
										</div>
									)}
								/>
							</div>
						</div>
					</Fragment>
				);
			})}
		</div>
	);
};

export default TrackingStepper;
